import React from 'react';
import { AlertCircle, CheckCircle2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

import { JSON_TEMPLATES } from '@/constants';

import {
  DATA_EXTRACTION_PROPERTY_TYPES,
  type DataExtractionPropertyFormValue,
  isEnumSupportedType,
} from '@/lib/data-extraction';
import { cn } from '@/lib/utils';

import {
  ARRAY_ITEM_TYPE_OPTIONS,
  getEnumHint,
  normalizeEnumValues,
  type PropertyDraftErrors,
} from './data-extraction-prompt.utils';

type DataExtractionDialogMode = 'manual' | 'json';

type DataExtractionPropertyDialogProps = {
  open: boolean;
  isEditing: boolean;
  mode: DataExtractionDialogMode;
  canUseManualMode: boolean;
  draft: DataExtractionPropertyFormValue;
  draftErrors: PropertyDraftErrors;
  jsonValue: string;
  jsonError?: string;
  selectedJsonTemplate: string | null;
  setDraft: React.Dispatch<React.SetStateAction<DataExtractionPropertyFormValue>>;
  onModeChange: (mode: DataExtractionDialogMode) => void;
  onJsonChange: (value: string) => void;
  onSelectJsonTemplate: (templateKey: string, templateValue: string) => void;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
  onApplyJson: () => void;
};

export const DataExtractionPropertyDialog = React.memo(
  ({
    open,
    isEditing,
    mode,
    canUseManualMode,
    draft,
    draftErrors,
    jsonValue,
    jsonError,
    selectedJsonTemplate,
    setDraft,
    onModeChange,
    onJsonChange,
    onSelectJsonTemplate,
    onOpenChange,
    onSave,
    onApplyJson,
  }: DataExtractionPropertyDialogProps) => {
    const supportsEnum = isEnumSupportedType(
      draft.dataType === 'array' ? draft.arrayItemType : draft.dataType
    );

    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{isEditing ? 'Edit field' : 'Add field'}</DialogTitle>
            <DialogDescription>
              Define a custom data field to extract from conversation transcripts.
            </DialogDescription>
          </DialogHeader>

          {!isEditing && (
            <div className="flex w-fit items-center gap-0.5 rounded-lg border bg-muted p-0.5">
              <button
                type="button"
                onClick={() => onModeChange('manual')}
                disabled={!canUseManualMode}
                className={cn(
                  'rounded-md px-4 py-1.5 text-sm font-medium transition-colors',
                  mode === 'manual'
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground',
                  !canUseManualMode && 'cursor-not-allowed opacity-50 hover:text-muted-foreground'
                )}
              >
                Manual
              </button>
              <button
                type="button"
                onClick={() => onModeChange('json')}
                className={cn(
                  'rounded-md px-4 py-1.5 text-sm font-medium transition-colors',
                  mode === 'json'
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                JSON
              </button>
            </div>
          )}

          {(mode === 'manual' || isEditing) && (
            <div className="space-y-6">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Data type</Label>
                  <Select
                    value={draft.dataType}
                    onValueChange={value => {
                      const nextType = value as DataExtractionPropertyFormValue['dataType'];
                      setDraft(current => ({
                        ...current,
                        dataType: nextType,
                        arrayItemType:
                          nextType === 'array' ? (current.arrayItemType ?? 'string') : null,
                        enumValues:
                          nextType === 'array' || isEnumSupportedType(nextType)
                            ? current.enumValues
                            : [],
                      }));
                    }}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select a type" />
                    </SelectTrigger>
                    <SelectContent>
                      {DATA_EXTRACTION_PROPERTY_TYPES.map(type => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="data-extraction-property-name">Identifier</Label>
                  <Input
                    id="data-extraction-property-name"
                    value={draft.identifier}
                    onChange={event => {
                      setDraft(current => ({
                        ...current,
                        identifier: event.target.value,
                      }));
                    }}
                    placeholder="customer_email"
                  />
                  {draftErrors.identifier && (
                    <p className="text-sm text-destructive">{draftErrors.identifier}</p>
                  )}
                </div>
              </div>

              {draft.dataType === 'array' && (
                <div className="space-y-2">
                  <Label>Array item type</Label>
                  <Select
                    value={draft.arrayItemType ?? undefined}
                    onValueChange={value => {
                      const nextItemType =
                        value as DataExtractionPropertyFormValue['arrayItemType'];
                      setDraft(current => ({
                        ...current,
                        arrayItemType: nextItemType,
                        enumValues: isEnumSupportedType(nextItemType) ? current.enumValues : [],
                      }));
                    }}
                  >
                    <SelectTrigger className="w-full sm:max-w-xs">
                      <SelectValue placeholder="Select an item type" />
                    </SelectTrigger>
                    <SelectContent>
                      {ARRAY_ITEM_TYPE_OPTIONS.map(type => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {draftErrors.arrayItemType && (
                    <p className="text-sm text-destructive">{draftErrors.arrayItemType}</p>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="data-extraction-property-description">Description</Label>
                <Textarea
                  id="data-extraction-property-description"
                  value={draft.description}
                  onChange={event => {
                    setDraft(current => ({
                      ...current,
                      description: event.target.value,
                    }));
                  }}
                  placeholder="Explain what this field should capture."
                  className="min-h-24"
                />
                <p className="text-xs text-muted-foreground">
                  This description is passed to the LLM and should clearly explain how to extract
                  the value.
                </p>
              </div>

              <label className="flex items-center gap-3 rounded-lg border px-3 py-2 text-sm">
                <input
                  type="checkbox"
                  checked={draft.required}
                  onChange={event => {
                    setDraft(current => ({
                      ...current,
                      required: event.target.checked,
                    }));
                  }}
                  className="h-4 w-4 rounded border-input"
                />
                Mark this field as required
              </label>

              {supportsEnum && (
                <div className="space-y-2">
                  <Label htmlFor="data-extraction-property-enum">Allowed values</Label>
                  <Textarea
                    id="data-extraction-property-enum"
                    value={draft.enumValues.join('\n')}
                    onChange={event => {
                      setDraft(current => ({
                        ...current,
                        enumValues: normalizeEnumValues(event.target.value),
                      }));
                    }}
                    placeholder="Enter one value per line"
                    className="min-h-24"
                  />
                  <p className="text-xs text-muted-foreground">{getEnumHint(draft)}</p>
                  {draftErrors.enumValues && (
                    <p className="text-sm text-destructive">{draftErrors.enumValues}</p>
                  )}
                </div>
              )}

              <DialogFooter>
                <Button variant="outline" type="button" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button type="button" onClick={onSave}>
                  {isEditing ? 'Update field' : 'Add field'}
                </Button>
              </DialogFooter>
            </div>
          )}

          {mode === 'json' && !isEditing && (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <span className="text-xs text-muted-foreground">Sample templates</span>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(JSON_TEMPLATES).map(([key, template]) => (
                    <button
                      key={key}
                      type="button"
                      title={template.description}
                      onClick={() => onSelectJsonTemplate(key, template.value)}
                      className={cn(
                        'rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
                        selectedJsonTemplate === key
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-border bg-muted text-muted-foreground hover:border-primary/50 hover:text-foreground'
                      )}
                    >
                      {template.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="data-extraction-json-schema">JSON schema</Label>
                <Textarea
                  id="data-extraction-json-schema"
                  value={jsonValue}
                  onChange={event => onJsonChange(event.target.value)}
                  placeholder={'{\n  "properties": []\n}'}
                  className="min-h-[220px] resize-y rounded-lg border bg-muted/40 p-3 font-mono text-xs leading-relaxed"
                  spellCheck={false}
                />
                <p className="text-xs text-muted-foreground">
                  Paste JSON schema directly or use a template. You can switch back to manual fields
                  when the resulting schema stays builder-compatible.
                </p>
              </div>

              {jsonError ? (
                <div className="flex items-start gap-1.5 text-xs text-destructive">
                  <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span>{jsonError}</span>
                </div>
              ) : (
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                  <span>Valid JSON</span>
                </div>
              )}

              {!canUseManualMode && !jsonError && (
                <p className="text-xs text-muted-foreground">
                  This schema uses structures the manual field builder cannot represent.
                </p>
              )}

              <DialogFooter>
                <Button variant="outline" type="button" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button type="button" onClick={onApplyJson} disabled={Boolean(jsonError)}>
                  Apply JSON schema
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    );
  }
);
